// =====================================================================
// src/screens/v2/NotificationsScreenV2.jsx
//
// V2 Merchant Notifications Inbox (Item #13 Phase A Step 8).
//
// Shows notifications with audience='business' — i.e. things the
// MERCHANT needs to know about (new customer connected, new order
// arrived, etc). Mirror-image of V1's NotificationsScreen, which
// filters audience='business' out and shows everything else to the
// customer side.
//
// Props (all driven by MerchantApp.jsx):
//   items         — array of notification docs (already filtered to
//                   audience='business', already ordered desc by createdAt)
//   loading       — true while the initial onSnapshot subscription is
//                   warming up (no items yet, may or may not become
//                   non-empty)
//   onBack        — close the screen (returns merchant to Today tab)
//   onMarkAllRead — bulk mark every visible unread item as read
//   onTapNotif    — called with the doc; parent handles marking as read
//                   AND any deepLink routing (Customers tab, Orders tab,
//                   etc.)
//
// Schema this component expects per item:
//   { id, kind, audience='business', actorId, actorName, actorPhoto,
//     text, subtext, businessId, orderId, deepLink: {...}, createdAt,
//     readAt }
//
// =====================================================================

import React from 'react';
import {
  ArrowLeft, Bell, Link2, FileText, BadgeCheck, Check,
} from 'lucide-react';
import './NotificationsScreenV2.css';

// ---- icon for a given notification kind ------------------------------
//
// Keep visually distinct so a busy merchant can skim. Each kind gets a
// coloured circular badge over (or next to) the actor avatar.
//
// Kinds currently written by Cloud Functions for audience='business':
//   - 'new-connect'       (onConnectionCreated)
//   - 'order-status'      (onOrderStatusChange, placed→confirmed only — the
//                          "new order arrived" moment for the merchant. The
//                          same kind covers customer-side order notifications
//                          under audience='member', but they're routed to V1.)
//   - 'connect-activated' (Session 6d — onActivationEvent, fired when an
//                          admin activates the business's Aangan Connect.
//                          Celebratory teal badge so it stands out from the
//                          routine connect/order rows.)
//
// Adding new kinds in Phase B (e.g. comment-on-post): extend this switch.
function kindIconForBusiness(kind) {
  switch (kind) {
    case 'new-connect':
      return { Icon: Link2, bg: '#dcfce7', fg: '#16a34a' };      // green
    case 'order-status':
      return { Icon: FileText, bg: '#dbeafe', fg: '#2563eb' };   // blue
    case 'connect-activated':
      return { Icon: BadgeCheck, bg: '#ccfbf1', fg: '#0d9488' }; // teal
    default:
      return { Icon: Bell, bg: '#fef3c7', fg: '#d97706' };       // amber fallback
  }
}

// ---- relative-time formatter -----------------------------------------
//
// Same approach as V1's formatNotifTimestamp: "Just now" / "5m" / "2h" /
// "yesterday" / "M/D/YYYY". Keep it lightweight; rely on the firestore
// Timestamp object's .toDate().
function formatNotifTime(ts) {
  if (!ts || !ts.toDate) return '';
  const d = ts.toDate();
  const diffMs = Date.now() - d.getTime();
  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return 'Just now';
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay === 1) return 'yesterday';
  if (diffDay < 7) return `${diffDay}d`;
  return d.toLocaleDateString();
}

// ---- single row UI ---------------------------------------------------
function NotifRow({ notif, onTap }) {
  const { Icon, bg, fg } = kindIconForBusiness(notif.kind);
  const isUnread = !notif.readAt;
  const initial = (notif.actorName || '?').charAt(0).toUpperCase();

  return (
    <button
      type="button"
      className={`v2m-notif-row ${isUnread ? 'unread' : ''}`}
      onClick={() => onTap(notif)}
    >
      {/* Actor avatar — denormalised actorPhoto, falls back to initial */}
      <div className="v2m-notif-avatar">
        {notif.actorPhoto ? (
          <img src={notif.actorPhoto} alt="" />
        ) : (
          <span className="v2m-notif-avatar-fallback">{initial}</span>
        )}
        {/* Kind badge overlay */}
        <span
          className="v2m-notif-kind-badge"
          style={{ background: bg, color: fg }}
          aria-hidden="true"
        >
          <Icon size={12} strokeWidth={2.5} />
        </span>
      </div>

      <div className="v2m-notif-body">
        <div className="v2m-notif-text">{notif.text || ''}</div>
        {notif.subtext && (
          <div className="v2m-notif-subtext">{notif.subtext}</div>
        )}
        <div className="v2m-notif-time">{formatNotifTime(notif.createdAt)}</div>
      </div>

      {isUnread && <span className="v2m-notif-unread-dot" aria-label="unread" />}
    </button>
  );
}

// ---- main screen -----------------------------------------------------
export default function NotificationsScreenV2({
  items = [],
  loading = false,
  onBack,
  onMarkAllRead,
  onTapNotif,
}) {
  const unreadCount = items.filter((n) => !n.readAt).length;

  return (
    <div className="v2-merchant v2m-notif-screen">
      {/* ---- header ---- */}
      <div className="v2m-notif-header">
        <button
          type="button"
          className="v2m-icon-btn"
          onClick={onBack}
          aria-label="Back"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="v2m-notif-title">Notifications</h1>
        {unreadCount > 0 && (
          <button
            type="button"
            className="v2m-notif-mark-all"
            onClick={onMarkAllRead}
          >
            <Check size={14} />
            <span>Mark all read</span>
          </button>
        )}
      </div>

      {/* ---- body ---- */}
      <div className="v2m-notif-list">
        {loading && items.length === 0 && (
          <div className="v2m-notif-empty">Loading…</div>
        )}

        {!loading && items.length === 0 && (
          <div className="v2m-notif-empty">
            <Bell size={28} strokeWidth={1.5} style={{ opacity: 0.4 }} />
            <p style={{ marginTop: 10 }}>No notifications yet.</p>
            <p
              style={{
                fontSize: 13,
                opacity: 0.7,
                marginTop: 4,
                textAlign: 'center',
              }}
            >
              You'll see new customer connects, orders, and updates here.
            </p>
          </div>
        )}

        {items.map((n) => (
          <NotifRow key={n.id} notif={n} onTap={onTapNotif} />
        ))}
      </div>
    </div>
  );
}
